/*
  cross-sell.js – krydssalgsblok til Hyper 1.3.1

  Kurv-opdatering uden cart:refresh (som i 1.3.1 ødelægger skuffens luk-knap):
  Blokken bruger samme vej som Hypers egen FoxKit-integration:
    1. 'cart:grouped-sections'  -> skuffe og kurvside melder deres section-id'er
    2. POST /cart/add.js med sections + sections_url
    3. FoxTheme.pubsub.publish(cartUpdate, { cart })
       -> Hyper opdaterer #CartDrawerBody/#CartDrawerFooter/#CartDrawerEmpty
          og #MainCart, og sender selv 'cart:updated'.

  Hyper-events der lyttes på:
    variant:changed  -> forvælg strømpestørrelse efter skostørrelse
    cart:updated     -> skjul varer der er kommet i kurven
*/
(() => {
  if (customElements.get('cross-sell-block')) return;

  /* ================= KONFIGURATION ================= */
  // true = kurv-skuffen åbner, når en vare lægges i kurven fra produktsiden.
  const OPEN_DRAWER_ON_ADD = true;
  // true under test: skriver til konsollen.
  const DEBUG = false;
  /* ================================================= */

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const seenViews = new Set();
  const log = (...a) => { if (DEBUG) console.log('[krydssalg]', ...a); };

  /* ---------- Måling: GA4 + Klaviyo + Shopify Customer Events ---------- */
  function track(name, params) {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
      else (window.dataLayer = window.dataLayer || []).push({ event: name, ...params });
    } catch (e) { /* ignore */ }
    try {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') window.klaviyo.push(['track', name, params]);
      else if (window._learnq) window._learnq.push(['track', name, params]);
    } catch (e) { /* ignore */ }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, params);
      }
    } catch (e) { /* ignore */ }
    log('event', name, params);
  }

  /* ---------- Hyper-integration ---------- */
  function hyperReady() {
    return !!(window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS && FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate);
  }

  // Samme mekanisme som Hypers CartItems/CartDiscount bruger
  function groupedSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return [...new Set(sections)].filter(Boolean).slice(0, 5);
  }

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  /* ---------- Størrelser ---------- */
  const toNum = (s) => parseFloat(String(s).replace(',', '.'));

  function shoeSize(value) {
    const m = String(value || '').match(/\d+(?:[.,]\d+)?/);
    return m ? toNum(m[0]) : null;
  }

  // "39/42", "39-42", "39 - 42" = interval. Ellers et enkelt tal.
  function fits(label, size) {
    const range = String(label).match(/(\d+(?:[.,]\d+)?)\s*[-–/]\s*(\d+(?:[.,]\d+)?)/);
    if (range) return size >= toNum(range[1]) && size <= toNum(range[2]);
    const single = String(label).match(/(?:^|[^\d])(\d+(?:[.,]\d+)?)(?:[^\d]|$)/);
    return single ? toNum(single[1]) === size : false;
  }

  class CrossSellBlock extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.handle;
      this.context = this.dataset.context;
      this.statusEl = this.querySelector('[data-cs-status]');

      this.onSubmit = (e) => {
        const form = e.target.closest('form[data-cs-form]');
        if (!form) return;
        e.preventDefault();
        this.add(form);
      };
      this.onChange = (e) => {
        if (!e.target.matches('select[data-cs-variant]')) return;
        e.stopPropagation(); // hold Hypers <cart-items> change-lytter udenfor
        e.target.dataset.touched = '1';
        this.updatePrice(e.target);
      };
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('change', this.onChange);

      this.sizeMap = null;
      const mapEl = this.querySelector('script[data-cs-size-map]');
      if (mapEl) {
        try { this.sizeMap = JSON.parse(mapEl.textContent); } catch (e) { this.sizeMap = null; }
      }
      if (this.dataset.sizeMatch) this.applySize(shoeSize(this.dataset.size));

      // Hyper 1.3.1 sender variant:changed med enten variant: null eller den
      // FORRIGE variant. Eventets indhold ignoreres derfor; det valgte variant-id
      // læses fra hovedproduktets formular (eller URL'en) lidt efter.
      this.onVariant = () => {
        if (!this.sizeMap) return;
        const readAndApply = () => {
          const info = document.querySelector('product-info[id^="MainProduct-"]');
          const form = info && document.getElementById(`product-form-${info.dataset.section}`);
          const input = form && form.querySelector('[name="id"]');
          const id = (input && input.value) || new URLSearchParams(window.location.search).get('variant');
          if (id && String(id) in this.sizeMap) this.applySize(shoeSize(this.sizeMap[String(id)]));
        };
        setTimeout(readAndApply, 150);
        setTimeout(readAndApply, 700);
      };
      document.addEventListener('variant:changed', this.onVariant);

      this.onCartUpdated = (e) => {
        const items = e.detail && e.detail.cart && e.detail.cart.items;
        if (!Array.isArray(items)) return;
        const inCart = new Set(items.map((i) => String(i.product_id)));
        this.items().forEach((li) => { if (inCart.has(li.dataset.productId)) li.remove(); });
        this.hideIfEmpty();
      };
      document.addEventListener('cart:updated', this.onCartUpdated);

      this.observeView();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:changed', this.onVariant);
      document.removeEventListener('cart:updated', this.onCartUpdated);
      if (this.io) this.io.disconnect();
    }

    items() { return Array.from(this.querySelectorAll('[data-cs-item]')); }
    productIds() { return this.items().map((li) => li.dataset.productId); }

    applySize(size) {
      if (!this.dataset.sizeMatch) return;
      this.querySelectorAll('select[data-cs-variant]').forEach((sel) => {
        if (sel.dataset.touched) return;
        const options = Array.from(sel.options).filter((o) => !o.disabled);
        const match = size != null ? options.find((o) => fits(o.dataset.title || o.textContent, size)) : null;
        const pick = match || options[0];
        if (pick && sel.value !== pick.value) {
          sel.value = pick.value;
          this.updatePrice(sel);
        }
      });
    }

    updatePrice(select) {
      const li = select.closest('[data-cs-item]');
      const priceEl = li && li.querySelector('[data-cs-price]');
      const opt = select.selectedOptions[0];
      if (priceEl && opt && opt.dataset.price) priceEl.textContent = opt.dataset.price;
    }

    observeView() {
      const send = () => {
        const ids = this.productIds();
        const key = `${this.context}:${this.handle}:${ids.join(',')}`;
        if (!ids.length || seenViews.has(key)) return;
        seenViews.add(key);
        track('cross_sell_view', {
          collection_handle: this.handle,
          context: this.context,
          product_ids: ids.join(','),
          source_product_id: this.dataset.sourceProductId || ''
        });
      };
      if (!('IntersectionObserver' in window)) { send(); return; }
      this.io = new IntersectionObserver((entries) => {
        if (entries.some((en) => en.isIntersecting)) { this.io.disconnect(); send(); }
      }, { threshold: 0.5 });
      this.io.observe(this);
    }

    announce(message, isError = false) {
      if (!this.statusEl) return;
      this.statusEl.textContent = message;
      this.statusEl.classList.toggle('cs__status--error', isError);
    }

    hideIfEmpty() {
      if (this.items().length) return;
      const list = this.querySelector('.cs__list');
      const heading = this.querySelector('.cs__heading');
      if (list) list.hidden = true;
      if (heading) heading.hidden = true;
      setTimeout(() => { this.hidden = true; }, 3000);
    }

    // Hypers onCartUpdate fanger fokus i skuffen. Er skuffen lukket, gives fokus tilbage til blokken.
    restoreFocus(fallback) {
      const drawer = getDrawer();
      if (drawer && drawer.open) return;
      try {
        if (window.FoxTheme && FoxTheme.a11y && typeof FoxTheme.a11y.removeTrapFocus === 'function') {
          FoxTheme.a11y.removeTrapFocus();
        }
      } catch (e) { /* ignore */ }
      const target = fallback && document.contains(fallback) ? fallback : this.statusEl;
      if (target) target.focus({ preventScroll: true });
    }

    async add(form) {
      const btn = form.querySelector('[data-cs-add]');
      const idInput = form.querySelector('[name="id"]');
      const li = form.closest('[data-cs-item]');
      if (!btn || !idInput || !li) return;

      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      this.announce('');

      const sections = hyperReady() ? groupedSections() : [];
      log('sections', sections);

      try {
        const res = await fetch(`${ROOT}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(idInput.value), quantity: 1 }],
            sections,
            sections_url: window.location.pathname
          })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.description || data.message || 'Varen kunne ikke lægges i kurven. Prøv igen.');

        track('cross_sell_add', {
          collection_handle: this.handle,
          context: this.context,
          product_id: li.dataset.productId,
          variant_id: idInput.value,
          source_product_id: this.dataset.sourceProductId || ''
        });

        // Næste vares knap får fokus, når denne forsvinder
        const nextItem = li.nextElementSibling || li.previousElementSibling;
        const nextBtn = nextItem && nextItem.querySelector('[data-cs-add]');
        const title = li.dataset.title;
        li.remove();
        this.announce(`${title} er lagt i kurven.`);
        this.hideIfEmpty();

        if (hyperReady()) {
          const cart = await (await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })).json();
          cart.sections = data.sections || {};
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
          log('cartUpdate publiceret', cart);

          if (this.context === 'product') {
            const drawer = getDrawer();
            if (OPEN_DRAWER_ON_ADD && drawer && typeof drawer.show === 'function') drawer.show();
            else this.restoreFocus(nextBtn);
          }
        } else {
          console.warn('[krydssalg] FoxTheme.pubsub ikke fundet – kurven opdateres ved næste sidevisning.');
        }
      } catch (err) {
        this.announce(err.message, true);
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('cross-sell-block', CrossSellBlock);

  /* ---------- Indsættelse i kurv-skuffe og kurvside ----------
     Hypers cart-drawer.liquid og main-cart.liquid ændres ikke. "Husk også"
     hentes som section 'cross-sell-cart' og sættes ind efter <cart-items>.
     Hyper udskifter body/MainCart ved hver kurvændring og sender så
     'cart:updated' – derefter sættes blokken ind igen. */
  const CART_SECTION = 'cross-sell-cart';
  let cartFetch = null;

  function cartTargets() {
    const targets = [];
    document.querySelectorAll('[id^="CartDrawerBody-"], [id^="MainCart-"]').forEach((container) => {
      const items = container.querySelector('cart-items');
      if (items && items.parentNode === container) {
        // Kurv-skuffen: direkte efter <cart-items> i #CartDrawerBody-…
        targets.push({ container, after: items });
      } else {
        // Kurvsiden: nederst i venstre kolonne (.cart__inner) under varelinjerne
        const inner = container.querySelector('.cart__inner');
        targets.push({ container: inner || container, after: null });
      }
    });
    return targets;
  }

  async function injectCartBlocks() {
    const targets = cartTargets();
    if (!targets.length) return;
    if (cartFetch) cartFetch.abort();
    cartFetch = new AbortController();
    try {
      const url = `${window.location.pathname}?section_id=${CART_SECTION}`;
      const res = await fetch(url, { signal: cartFetch.signal });
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('cross-sell-block');
      targets.forEach(({ container, after }) => {
        container.querySelectorAll('[data-cs-cart-slot]').forEach((el) => el.remove());
        if (!fresh) return;
        const slot = document.createElement('div');
        slot.setAttribute('data-cs-cart-slot', '');
        slot.appendChild(document.importNode(fresh, true));
        if (after) after.after(slot);
        else container.appendChild(slot);
      });
      log('kurv-blok indsat', targets.length);
    } catch (e) {
      if (e.name !== 'AbortError') log('kurv-blok fejl', e);
    }
  }

  document.addEventListener('cart:updated', () => injectCartBlocks());
  document.addEventListener('variant:changed', () => {
    if (typeof window.CrossSellInsertProduct === 'function') setTimeout(window.CrossSellInsertProduct, 50);
  });

  const hasItems = document.documentElement.classList.contains('cart-has-items');
  const onCartPage = !!document.querySelector('[id^="MainCart-"]');
  if (hasItems || onCartPage) {
    const run = () => injectCartBlocks();
    if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 3000 });
    else setTimeout(run, 1500);
  }
})();
